import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import 'katex/dist/katex.min.css';
import { normalizeMathMarkdown } from '../../../../utils/mathText';
interface MarkdownRendererProps {
    content: string;
}

/* ============================================================
   MODERN MARKDOWN COMPONENTS
   Using semantic HTML elements styled with Tailwind typography
============================================================ */

const markdownClassName = [
    'prose prose-sm max-w-none',
    'text-inherit',
    'prose-headings:text-inherit prose-headings:font-semibold prose-headings:tracking-normal',
    'prose-p:my-3 prose-p:leading-7',
    'prose-strong:text-inherit prose-strong:font-semibold',
    'prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline',
    'prose-ul:my-3 prose-ol:my-3 prose-li:my-1',
    'prose-blockquote:my-4 prose-blockquote:rounded-r prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:bg-blue-500/10 prose-blockquote:px-4 prose-blockquote:py-2 prose-blockquote:not-italic',
    'prose-hr:my-5 prose-hr:border-slate-200',
    'prose-table:block prose-table:w-max prose-table:max-w-full prose-table:overflow-auto',
    'prose-th:border prose-th:border-slate-200 prose-th:px-3 prose-th:py-2',
    'prose-td:border prose-td:border-slate-200 prose-td:px-3 prose-td:py-2',
    'prose-code:rounded prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:text-[0.85em] prose-code:font-normal prose-code:before:content-none prose-code:after:content-none',
    'prose-pre:my-4 prose-pre:overflow-x-auto prose-pre:rounded-md prose-pre:border prose-pre:border-slate-700 prose-pre:bg-slate-950 prose-pre:p-0',
    'prose-img:max-w-full prose-img:rounded-md',
    'dark:prose-invert dark:prose-a:text-blue-300 dark:prose-code:bg-slate-800 dark:prose-th:border-slate-700 dark:prose-td:border-slate-700',
    '[&_.katex-display]:my-4 [&_.katex-display]:overflow-x-auto [&_.katex-display]:overflow-y-hidden [&_.katex-display]:py-2',
    '[&_.katex]:whitespace-normal [&_.katex]:text-[1.04em]',
    '[&_.katex-html]:overflow-x-auto',
].join(' ');

const MarkdownComponents = {
    // Code blocks with syntax highlighting
    code({ node, inline, className, children, ...props }: any) {
        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : '';

        if (!inline && language) {
            return (
                <div className="my-4 overflow-hidden rounded-md shadow-sm">
                    <div className="bg-slate-800 px-4 py-2 font-mono text-xs text-slate-300">
                        {language}
                    </div>
                    <SyntaxHighlighter
                        style={vscDarkPlus}
                        language={language}
                        PreTag="div"
                        showLineNumbers={true}
                        customStyle={{ margin: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
                        {...props}
                    >
                        {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                </div>
            );
        }

        // Inline code
        return (
            <code {...props}>
                {children}
            </code>
        );
    }
};

/* ============================================================
   MAIN COMPONENT
============================================================ */

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
    // Shared normalizer: \( \) / \[ \] -> $ / $$, repairs JSON-mangled LaTeX,
    // keeps prices as text and leaves code blocks untouched.
    const processedContent = normalizeMathMarkdown(content);

    return (
        <div className={markdownClassName}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: false }], rehypeRaw]}
                components={MarkdownComponents}
            >
                {processedContent}
            </ReactMarkdown>
        </div>
    );
};

export default MarkdownRenderer;
